<template>
    <promotion-dates v-bind="form" :cache-dom="cacheDom" />
</template>

<script>
import { isEmpty, pick } from 'lodash';
import { mapState } from 'vuex';
import vuexForm from '@enums/vuex-form';
import validators from '@/js/validators';
import promotionDateRangeUtils from '@sharedModules/promotion-date-range-utils';
import createFeatureAwareFactory from '@/js/feature-toggles/feature-factory';
import promotionTabFormWrapperMixin from '@/js/pages/planning/components/mixins/promotion-tab-form-wrapper';

const { fieldTypes, sourceTypes } = vuexForm;
const { getPromotionDateBounds } = promotionDateRangeUtils;

export default {
    mixins: [promotionTabFormWrapperMixin],
    props: {
        isParkingLot: {
            type: Boolean,
            default: false,
        },
        pendingChangesToReview: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState('clientConfig', ['toggleLogic']),
        ...mapState('promotions', ['stagingArea']),

        featureAwareFactory() {
            return createFeatureAwareFactory(this.toggleLogic);
        },

        stagingAreaNamespace() {
            return this.editMode ? this.context.selectedPromotion._id : this.namespace;
        },

        stagingAreaPromotionDates() {
            return pick(this.stagingArea[this.stagingAreaNamespace] || {}, [
                'startDate',
                'endDate',
            ]);
        },

        form() {
            let field = {
                type: fieldTypes.dateRange,
                from: {
                    fieldName: 'startDate',
                    dynamicDefaultValue: {
                        source: sourceTypes.context,
                        path: 'selectedPromotion.startDate',
                    },
                },
                to: {
                    fieldName: 'endDate',
                    dynamicDefaultValue: {
                        source: sourceTypes.context,
                        path: 'selectedPromotion.endDate',
                    },
                },
                minDate: {
                    source: sourceTypes.context,
                    path: this.$moment,
                },
                editable: true,
                class: 'promotion-period__calendar',
                validations: [
                    {
                        validator: validators.required,
                    },
                    {
                        validator: validators.dateRangeRequired,
                    },
                ],
                theme: 'white',
                skipComputedMin: true,
            };
            if (!isEmpty(this.context.selectedScenario)) {
                const promotionDateRangeOptions = this.featureAwareFactory.getPromotionDateRangeOptions();
                const getScenarioDateBounds = () =>
                    getPromotionDateBounds({
                        scenario: this.context.selectedScenario,
                        ...promotionDateRangeOptions,
                    });

                field = {
                    ...field,
                    minDate: {
                        source: sourceTypes.contextFunction,
                        contextFunction: () => {
                            const dates = [getScenarioDateBounds().minDate, this.$moment()].map(
                                date => this.$moment(date)
                            );
                            return this.datepickerFormat(this.$moment.max(dates));
                        },
                    },
                    maxDate: this.datepickerFormat(getScenarioDateBounds().maxDate),
                };

                if (promotionDateRangeOptions.allowOutsideScenario) {
                    field.validations = [
                        ...field.validations,
                        {
                            validator: validators.promotionDatesWithinScenario,
                            params: [
                                () => this.stagingAreaPromotionDates,
                                () => this.context.selectedScenario,
                                promotionDateRangeOptions,
                            ],
                        },
                    ];
                }
            }
            // if promotion startDate is in the past, don't allow user to change startDate.
            const { startDate } = this.context.selectedPromotion;
            if (
                !this.isParkingLot &&
                this.$moment(startDate).isBefore(this.$moment().startOf('day'))
            ) {
                field.fixedStartDate = startDate;
            }
            const { isInManualMode, parentPromotionId } = this.context.selectedPromotion;

            if ((parentPromotionId && !isInManualMode) || this.pendingChangesToReview) {
                field.editable = false;
            }

            return {
                vuexModule: 'promotions',
                editMode: this.editMode,
                submitAction: this.isNewPromotion ? null : 'prepareAndUpdatePromotion',
                preventDefault: true,
                namespace: this.namespace,
                context: this.context,
                editContext: this.context.selectedPromotion,
                fields: [field],
            };
        },
    },
};
</script>
